import {
  Drawer,
  Input,
  Button,
  Tooltip,
  Form,
  Radio,
  message,
  Col,
  Grid,
  InputNumber,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useCreateRFID } from '../../Pages/rfid-manager/Hook';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useQueryClient } from '@tanstack/react-query';
import {
  DebouncedSelect,
  fetchChargerList,
  fetchLocationList,
  fetchCustomerList, // Import fetchCustomerList
} from '../useDebouncedSelectLocation';
import PropTypes from 'prop-types';

const { useBreakpoint } = Grid;

const AddRFIDDrawer = ({ open, setClose }) => {
  // Removed assignedTo
  const { t } = useTranslation('RFIDDrawer');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { mutate: createRFID, isPending } = useCreateRFID();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { operatorId } = useOperatorStore();
  const [rfidScope, setRFIDScope] = useState('global');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCharger, setSelectedCharger] = useState(null);

  const handleScopeChange = (e) => {
    setRFIDScope(e.target.value);
    form.setFieldsValue({ locationId: "", chargersId: "" });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const locationIdRaw = values.locationId;
      const chargersIdRaw = values.chargersId;
      const isPrePaid = !!values.prePaid;
      const formattedValues = {
        RFID: values.RFID.trim(),
        operatorId,
        ...(values.customerId && { customerId: String(values.customerId) }),
        locationId: rfidScope === 'location' && locationIdRaw ? [String(locationIdRaw)] : [],
        chargersId: rfidScope === 'charger' && chargersIdRaw ? [String(chargersIdRaw)] : [],
        status: 'Accepted',
        description: values.description?.trim() || '',
        isGlobal: rfidScope === 'global',
        prePaid: isPrePaid,
        ...(isPrePaid
          ? { balance: (Number(values.balance) || 0) * 1000 }
          : { balance: 0, limit: (Number(values.balance) || 0) * 1000 }),
      };
      createRFID(formattedValues, {
        onSuccess: () => {
          queryClient.invalidateQueries(['fetch-RFID', operatorId]);
          message.success(t('RFIDDrawer.SuccessMessage'));
          form.resetFields();
          setClose();
        },
        onError: (error) => {
          message.error(
            error?.response?.data?.message || t('RFIDDrawer.ErrorMessage')
          );
        },
      });
    } catch (errorInfo) {
      console.error('Validation Failed:', errorInfo);
    }
  };

  return (
    <Drawer
      maskClosable={false}
      destroyOnHidden
      footer={
        <div className="flex justify-start gap-2 mt-4">
          <Button
            htmlType="submit"
            loading={isPending}
            type="primary"
            onClick={handleSubmit}
          >
            {t('RFIDDrawer.SubmitButton')}
          </Button>
          <Button disabled={isPending} onClick={setClose}>
            {t('RFIDDrawer.CancelButton')}
          </Button>
        </div>
      }
      open={open}
      title={t('RFIDDrawer.Title')}
      size={isMobile ? '100%' : 600}
      onClose={() => {
        form.resetFields();
        setClose();
      }}
    >
      <Form
        form={form}
        layout="vertical"
        className='space-y-4'
        initialValues={{
          RFID: '',
          locationId: undefined,
          chargersId: undefined,
          description: '',
          isGlobal: false,
          prePaid: false,
          balance: 0,
        }}
      >
        {/* RFID Field */}
        <Col span={24}>
          <Form.Item
            name="RFID"
            label={
              <>
                {t('RFIDDrawer.RFIDLabel')}{' '}
                <Tooltip title={t('RFIDDrawer.RFIDTooltip')}>
                  <InfoCircleOutlined className="text-gray-500 ml-1" />
                </Tooltip>
              </>
            }
            rules={[
              {
                required: true,
                message: t('RFIDDrawer.Validation.RFIDRequired'),
              },
              { max: 20, message: t('RFIDDrawer.Validation.RFIDMax') },
              {
                validator: (_, value) =>
                  value && value.trim() !== ''
                    ? Promise.resolve()
                    : Promise.reject(
                      new Error(t('RFIDDrawer.Validation.RFIDNoSpaces'))
                    ),
              },
            ]}
          >
            <Input placeholder={t('RFIDDrawer.RFIDPlaceholder')} />
          </Form.Item>
        </Col>

        {/* Assign To (Resident Select) */}
        <Col span={24}>
          <Form.Item
            name="customerId"
            label={
              <>
                {t('RFIDDrawer.AssignToLabel')}{' '}
                <Tooltip title={t('RFIDDrawer.AssignToTooltip')}>
                  <InfoCircleOutlined className="text-gray-500 ml-1" />
                </Tooltip>
              </>
            }
            rules={[
              {
                required: true,
                message: t('RFIDDrawer.Validation.AssignToRequired'),
              },
            ]}
          >
            <DebouncedSelect
              placeholder={t('RFIDDrawer.searchCustomer')}
              fetchOptions={(search) =>
                fetchCustomerList({ operatorId, search })
              }
              allowClear
            />
          </Form.Item>
        </Col>

        {/* Activation Scope (Global, Location, Charger, Attach Later) */}
        <Col span={24}>
          <Form.Item
            label={
              <>
                {t('RFIDDrawer.ActivationScopeLabel')}{' '}
                <Tooltip title={t('RFIDDrawer.ScopeTooltip')}>
                  <InfoCircleOutlined className="text-gray-500 ml-1" />
                </Tooltip>
              </>
            }
            required
          >
            <Radio.Group onChange={handleScopeChange} value={rfidScope}>
              <Radio value="global">{t('RFIDDrawer.Scope.Global')}</Radio>
              <Radio value="location">{t('RFIDDrawer.Scope.Location')}</Radio>
              <Radio value="charger">{t('RFIDDrawer.Scope.Charger')}</Radio>
              <Radio value="later">{t('RFIDDrawer.Scope.AttachLater')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {/* Location Select (Shown if Location is Selected) */}
        {rfidScope === 'location' && (
          <Col span={24}>
            <Form.Item
              name="locationId"
              label={t('RFIDDrawer.LocationLabel')}
              rules={[
                {
                  required: true,
                  message: t('RFIDDrawer.Validation.LocationRequired'),
                },
              ]}
            >
              <DebouncedSelect
                placeholder={t('RFIDDrawer.searchLocation')}
                fetchOptions={(search) =>
                  fetchLocationList({ operatorId, search })
                }
                value={selectedLocation}
                onChange={setSelectedLocation}
              />
            </Form.Item>
          </Col>
        )}

        {/* Charger Select (Shown if Charger is Selected) */}
        {rfidScope === 'charger' && (
          <Col span={24}>
            <Form.Item
              name="chargersId"
              label={t('RFIDDrawer.ChargerLabel')}
              rules={[
                {
                  required: true,
                  message: t('RFIDDrawer.Validation.ChargerRequired'),
                },
              ]}
            >
              <DebouncedSelect
                placeholder={t('RFIDDrawer.searchCharger')}
                fetchOptions={(search) =>
                  fetchChargerList({ operatorId, search })
                }
                value={selectedCharger}
                onChange={setSelectedCharger}
              />
            </Form.Item>
          </Col>
        )}

        {/* Description Field */}
        <Col span={24}>
          <Form.Item
            name="description"
            label={t('RFIDDrawer.DescriptionLabel')}
            rules={[
              {
                max: 100,
                message: t('RFIDDrawer.Validation.DescriptionMax'),
              },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder={t('RFIDDrawer.DescriptionPlaceholder')}
            />
          </Form.Item>
        </Col>

        {/* Pre-paid & Balance */}
        <Col span={24}>
          <Form.Item
            name="prePaid"
            label={t('RFIDDrawer.PaymentType')}
            tooltip={t('RFIDDrawer.PrePaidTooltip')}
            initialValue={true}
          >
            <Radio.Group>
              <Radio value={true}>{t('RFIDDrawer.PrePaidLabel')}</Radio>
              <Radio value={false}>{t('RFIDDrawer.PostPaid')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {/* Balance / Limit Field */}
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.prePaid !== currentValues.prePaid}
        >
          {({ getFieldValue }) => {
            const isPrePaid = getFieldValue('prePaid');
            return (
              <Col span={24}>
                <Form.Item
                  name="balance"
                  label={isPrePaid ? t('RFIDDrawer.CreditsLabel') : t('RFIDDrawer.LimitLabel')}
                  initialValue={0}
                  tooltip={isPrePaid ? t('RFIDDrawer.CreditsTooltip') : t('RFIDDrawer.LimitTooltip')}
                  extra={isPrePaid ? t('RFIDDrawer.CreditsDescription') : t('RFIDDrawer.LimitDescription')}
                  rules={[
                    {
                      required: isPrePaid,
                      message: t('RFIDDrawer.Validation.BalanceRequired'),
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: t('RFIDDrawer.Validation.BalanceMin'),
                    },
                  ]}
                >
                  <InputNumber min={0} step={0.01} style={{ width: '100%', height: '38.4px' }} placeholder="kW" />
                </Form.Item>
              </Col>
            );
          }}
        </Form.Item>

      </Form>
    </Drawer>
  );
};

AddRFIDDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setClose: PropTypes.func.isRequired,
};

export default AddRFIDDrawer;
